import { useState, useEffect } from 'react';
import { channelsAPI, merchantAPI, connectAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { Radio, Plus, QrCode, Unplug, Plug, Trash2, Settings, X, Copy, Check, AlertTriangle, ShieldOff, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';

function CopyableUrl({ label, url }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { toast.error('Failed to copy'); }
  };
  return (
    <div style={{ marginTop: 6, marginBottom: 8 }}>
      {label && <div style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>}
      <div onClick={handleCopy} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        cursor: 'pointer', transition: 'all 0.15s ease',
      }}>
        <code style={{ flex: 1, fontSize: '0.75rem', color: 'var(--text)', wordBreak: 'break-all', lineHeight: 1.4 }}>{url}</code>
        {copied
          ? <Check size={14} style={{ flexShrink: 0, color: 'var(--green)' }} />
          : <Copy size={14} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
        }
      </div>
    </div>
  );
}

const WEBHOOK_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');

// Brand SVG icons
const WhatsAppIcon = ({ size = 20, color = '#25D366' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TelegramIcon = ({ size = 20, color = '#0088cc' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const FacebookIcon = ({ size = 20, color = '#1877F2' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = ({ size = 20, color = '#E4405F' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/>
  </svg>
);

const WebChatIcon = ({ size = 20, color = '#6366f1' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
    <circle cx="9" cy="10" r="1" fill={color} stroke="none"/>
    <circle cx="12" cy="10" r="1" fill={color} stroke="none"/>
    <circle cx="15" cy="10" r="1" fill={color} stroke="none"/>
  </svg>
);

const KakaoTalkIcon = ({ size = 20, color = '#FAE100' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 1C5.373 1 0 5.244 0 10.5c0 3.394 2.262 6.382 5.668 8.053-.247.93-.896 3.37-.929 3.573 0 0-.019.158.084.218.103.06.224.013.224.013.296-.041 3.434-2.267 3.973-2.652A14.32 14.32 0 0012 20c6.627 0 12-4.244 12-9.5S18.627 1 12 1z"/>
  </svg>
);

const TikTokIcon = ({ size = 20, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.52a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.62a8.2 8.2 0 004.76 1.52V6.69h-1z"/>
  </svg>
);

const CHANNEL_ICONS = {
  whatsapp: { color: '#25D366', bg: 'rgba(37,211,102,0.12)', label: 'WhatsApp', Icon: WhatsAppIcon },
  telegram: { color: '#0088cc', bg: 'rgba(0,136,204,0.12)', label: 'Telegram', Icon: TelegramIcon },
  facebook: { color: '#1877F2', bg: 'rgba(24,119,242,0.12)', label: 'Facebook', Icon: FacebookIcon },
  instagram: { color: '#E4405F', bg: 'rgba(228,64,95,0.12)', label: 'Instagram', Icon: InstagramIcon },
  webchat: { color: '#6366f1', bg: 'rgba(99,102,241,0.12)', label: 'Web Chat', Icon: WebChatIcon },
  // Hidden for now — enable later
  // kakaotalk: { color: '#3C1E1E', bg: 'rgba(250,225,0,0.15)', label: 'KakaoTalk', Icon: KakaoTalkIcon },
  // tiktok: { color: '#000000', bg: 'rgba(0,0,0,0.08)', label: 'TikTok', Icon: TikTokIcon },
};

const defaultNewChannel = {
  name: '', type: 'whatsapp', connectionType: 'aina', accessType: 'public',
  whatsappOfficial: { phoneNumberId: '', accessToken: '', wabaId: '', verifyToken: '' },
  telegram: { botToken: '' },
  facebook: { pageId: '', accessToken: '', verifyToken: '' },
  instagram: { accountId: '', accessToken: '', verifyToken: '' },
  webchat: { widgetColor: '#6366f1', welcomeMessage: 'Hi! How can we help you?', placeholder: 'Type a message...', position: 'right', allowedOrigins: [''], allowLiveAgent: true, bubbleIcon: '', bubbleSize: 60, prechatForm: { enabled: false, mode: 'required', title: 'Before we start', subtitle: 'Please share a few details so we can help you better.', submitLabel: 'Start Chat', skipLabel: 'Skip and chat anonymously', fields: [
    { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your name' },
    { key: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' },
    { key: 'phone', label: 'Phone', type: 'tel', required: false, placeholder: '+1 555 000 0000' },
  ] } },
  kakaotalk: { appKey: '', botId: '', channelPublicId: '', adminKey: '' },
  tiktok: { appId: '', appSecret: '', accessToken: '', businessId: '', webhookSecret: '' },
};

export default function ChannelsPage() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showQR, setShowQR] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [showSettings, setShowSettings] = useState(null);
  const [planLimits, setPlanLimits] = useState({ maxChannels: 10, channelTypeLimits: {}, allowedChannels: null });
  const { on, off } = useSocket();
  const { user } = useAuth();
  const [connectedApps, setConnectedApps] = useState({}); // channelId -> connection

  const [newChannel, setNewChannel] = useState({ ...defaultNewChannel });

  useEffect(() => {
    fetchChannels();
    fetchConnectedApps();
    merchantAPI.getMe().then(({ data }) => {
      const m = data.data || data;
      setPlanLimits({
        maxChannels: m.limits?.maxChannels ?? 10,
        channelTypeLimits: m.channelTypeLimits || {},
        allowedChannels: m.allowedChannels?.length ? m.allowedChannels : null,
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleQR = (data) => {
      if (data.channelId === showQR) setQrData(data.qrCode);
      setChannels(prev => prev.map(c => c._id === data.channelId ? { ...c, status: 'pending_qr' } : c));
    };
    const handleConnected = (data) => {
      setChannels(prev => prev.map(c => c._id === data.channelId ? { ...c, status: 'connected' } : c));
      setShowQR(null);
      toast.success('Channel connected!');
    };
    on('whatsapp:qr', handleQR);
    on('whatsapp:connected', handleConnected);
    return () => { off('whatsapp:qr', handleQR); off('whatsapp:connected', handleConnected); };
  }, [on, off, showQR]);

  const fetchConnectedApps = async () => {
    try {
      const { data } = await connectAPI.listApps();
      setConnectedApps(Object.fromEntries(data.data.map(a => [a.channelId, a])));
    } catch (_) {}
  };

  const handleRevokeApp = async (conn) => {
    if (!window.confirm(`Disconnect ${conn.app?.name || 'this app'}? It will stop receiving replies from "${conn.channelName}".`)) return;
    try {
      await connectAPI.revokeApp(conn.channelId);
      toast.success('App disconnected');
      fetchConnectedApps();
      fetchChannels();
    } catch (_) {}
  };

  const fetchChannels = async () => {
    try {
      const { data } = await channelsAPI.list();
      setChannels(data.data);
    } catch (_) {}
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newChannel.name.trim()) return toast.error('Channel name required');

    // Validate channel-specific fields
    if (newChannel.type === 'whatsapp' && newChannel.connectionType === 'official') {
      if (!newChannel.whatsappOfficial.phoneNumberId.trim() || !newChannel.whatsappOfficial.accessToken.trim()) {
        return toast.error('Phone Number ID and Access Token are required for Official API');
      }
      if (!newChannel.whatsappOfficial.verifyToken.trim()) {
        return toast.error('Webhook Verify Token is required');
      }
    }
    if (newChannel.type === 'telegram' && !newChannel.telegram.botToken.trim()) {
      return toast.error('Telegram Bot Token is required');
    }
    if (newChannel.type === 'facebook' && (!newChannel.facebook.pageId.trim() || !newChannel.facebook.accessToken.trim())) {
      return toast.error('Facebook Page ID and Access Token are required');
    }
    if (newChannel.type === 'facebook' && !newChannel.facebook.verifyToken.trim()) {
      return toast.error('Webhook Verify Token is required');
    }
    if (newChannel.type === 'instagram' && (!newChannel.instagram.accountId.trim() || !newChannel.instagram.accessToken.trim())) {
      return toast.error('Instagram Account ID and Access Token are required');
    }
    if (newChannel.type === 'instagram' && !newChannel.instagram.verifyToken.trim()) {
      return toast.error('Webhook Verify Token is required');
    }
    if (newChannel.type === 'kakaotalk' && (!newChannel.kakaotalk.appKey.trim() || !newChannel.kakaotalk.adminKey.trim())) {
      return toast.error('KakaoTalk App Key and Admin Key are required');
    }
    if (newChannel.type === 'tiktok' && (!newChannel.tiktok.appId.trim() || !newChannel.tiktok.appSecret.trim() || !newChannel.tiktok.accessToken.trim())) {
      return toast.error('TikTok App ID, App Secret, and Access Token are required');
    }
    if (newChannel.type === 'webchat') {
      const origins = newChannel.webchat.allowedOrigins.map(u => u.trim()).filter(Boolean);
      if (origins.length === 0) return toast.error('At least one website URL is required');
      const invalid = origins.find(u => !u.startsWith('http://') && !u.startsWith('https://'));
      if (invalid) return toast.error('Website URL must start with http:// or https://');
      newChannel.webchat.allowedOrigins = origins;
    }

    try {
      const payload = { ...newChannel };
      if (payload.type === 'whatsapp' && payload.connectionType === 'official') {
        payload.whatsappOfficial = newChannel.whatsappOfficial;
      }
      const { data } = await channelsAPI.create(payload);
      setChannels(prev => [...prev, data.data]);
      setShowAdd(false);
      setNewChannel({ ...defaultNewChannel });
      toast.success('Channel created');
    } catch (err) {
      // axios interceptor already shows toast for non-404 errors
    }
  };

  const handleConnect = async (channel) => {
    setConnecting(channel._id);
    try {
      const { data } = await channelsAPI.connect(channel._id);
      if (channel.type === 'whatsapp' && channel.whatsapp?.connectionType === 'aina') {
        setShowQR(channel._id);
        setQrData(null);
        toast.success('Scan the QR code with WhatsApp');
      } else {
        setChannels(prev => prev.map(c => c._id === channel._id ? { ...c, status: 'connected' } : c));
        toast.success(data.message || 'Channel connected!');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Connection failed');
    }
    finally { setConnecting(null); }
  };

  const handleDisconnect = async (id) => {
    try {
      await channelsAPI.disconnect(id);
      setChannels(prev => prev.map(c => c._id === id ? { ...c, status: 'disconnected' } : c));
      toast.success('Channel disconnected');
    } catch (_) {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this channel? This cannot be undone.')) return;
    try {
      await channelsAPI.delete(id);
      setChannels(prev => prev.filter(c => c._id !== id));
      toast.success('Channel deleted');
    } catch (_) {}
  };

  const handleUpdateSettings = async (channelId, updates) => {
    try {
      const { data } = await channelsAPI.update(channelId, updates);
      setChannels(prev => prev.map(c => c._id === channelId ? data.data : c));
      toast.success('Settings saved');
      setShowSettings(null);
    } catch (_) {
      toast.error('Failed to save settings');
    }
  };

  const fetchQR = async (channelId) => {
    try {
      const { data } = await channelsAPI.getQR(channelId);
      setQrData(data.data.qrCode);
      setShowQR(channelId);
    } catch (err) {
      toast.error('QR not available. Try reconnecting.');
    }
  };

  const statusBadge = (status) => {
    const map = { connected: 'badge-green', disconnected: 'badge-gray', error: 'badge-red', pending_qr: 'badge-yellow', connecting: 'badge-blue' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}><span className={`status-dot ${status}`} />{status.replace('_', ' ')}</span>;
  };

  const getChannelDetail = (ch) => {
    if (ch.type === 'whatsapp') {
      return (
        <>
          {ch.whatsapp?.aina?.phoneNumber && <span>Phone: {ch.whatsapp.aina.phoneNumber}</span>}
          {ch.whatsapp?.official?.phoneNumber && <span>Phone: {ch.whatsapp.official.phoneNumber}</span>}
          {ch.whatsapp?.connectionType && <span className="badge badge-gray">{ch.whatsapp.connectionType}</span>}
        </>
      );
    }
    if (ch.type === 'telegram') {
      return ch.telegram?.botUsername ? <span>@{ch.telegram.botUsername}</span> : null;
    }
    if (ch.type === 'facebook') {
      return ch.facebook?.pageName ? <span>{ch.facebook.pageName}</span> : null;
    }
    if (ch.type === 'instagram') {
      return ch.instagram?.username ? <span>@{ch.instagram.username}</span> : null;
    }
    if (ch.type === 'kakaotalk') {
      return ch.kakaotalk?.channelPublicId ? <span>ID: {ch.kakaotalk.channelPublicId}</span> : null;
    }
    if (ch.type === 'tiktok') {
      return ch.tiktok?.businessId ? <span>Business: {ch.tiktok.businessId}</span> : null;
    }
    return null;
  };

  const channelInfo = CHANNEL_ICONS;

  // Derive usage from channels + planLimits
  const totalUsed = channels.length;
  const maxChannels = planLimits.maxChannels;
  const totalAtLimit = maxChannels !== -1 && totalUsed >= maxChannels;
  const usagePct = maxChannels === -1 ? 0 : Math.min(100, Math.round((totalUsed / maxChannels) * 100));

  // Count channels by type
  const countByType = {};
  channels.forEach(ch => { countByType[ch.type] = (countByType[ch.type] || 0) + 1; });

  // Which channel types are blocked by per-type limits
  const typeAtLimit = (type) => {
    const limit = planLimits.channelTypeLimits?.[type] || 0;
    return limit > 0 && (countByType[type] || 0) >= limit;
  };
  const typeNotAllowed = (type) => planLimits.allowedChannels && !planLimits.allowedChannels.includes(type);

  // Visible channel types (allowed by plan or all if no restriction)
  const visibleChannelIcons = Object.entries(CHANNEL_ICONS).filter(([key]) => !typeNotAllowed(key));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Channels</h1>
          <p className="page-subtitle">Connect communication channels to receive messages</p>
        </div>
        <button className="btn btn-primary" onClick={() => {
          // Auto-select first allowed type so form fields are always consistent
          const firstAllowed = visibleChannelIcons[0]?.[0] || 'whatsapp';
          setNewChannel({ ...defaultNewChannel, type: firstAllowed });
          setShowAdd(true);
        }} disabled={totalAtLimit} title={totalAtLimit ? 'Channel limit reached' : ''}>
          <Plus size={15} /> Add Channel
        </button>
      </div>

      <div className="page-body">
        {/* Plan Usage Banner */}
        <div style={{ marginBottom: 20, padding: '14px 18px', borderRadius: 10, border: `1px solid ${totalAtLimit ? 'var(--red)' : 'var(--border)'}`, background: totalAtLimit ? 'rgba(239,68,68,0.05)' : 'var(--card-bg)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Channel Usage</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: totalAtLimit ? 'var(--red)' : 'var(--text)' }}>
                {totalUsed} / {maxChannels === -1 ? '∞' : maxChannels}
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 4, width: `${usagePct}%`, background: totalAtLimit ? 'var(--red)' : usagePct > 75 ? '#f59e0b' : 'var(--primary)', transition: 'width 0.4s' }} />
            </div>
          </div>
          {/* Per-type usage pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {visibleChannelIcons.map(([key, info]) => {
              const used = countByType[key] || 0;
              if (used === 0 && !planLimits.channelTypeLimits?.[key]) return null;
              const limit = planLimits.channelTypeLimits?.[key] || 0;
              const atLimit = limit > 0 && used >= limit;
              return (
                <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, background: atLimit ? 'rgba(239,68,68,0.1)' : info.bg, color: atLimit ? 'var(--red)' : info.color, border: `1px solid ${atLimit ? 'rgba(239,68,68,0.3)' : info.color + '30'}` }}>
                  {atLimit && <ShieldOff size={9} />}
                  {info.label}: {used}{limit > 0 ? `/${limit}` : ''}
                </span>
              );
            })}
          </div>
          {totalAtLimit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--red)', fontWeight: 600 }}>
              <AlertTriangle size={14} /> Channel limit reached — upgrade your plan to add more
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex-center" style={{ padding: 60 }}><div className="spinner" /></div>
        ) : channels.length === 0 ? (
          <div className="empty-state card">
            <Radio size={40} />
            <h3>No channels yet</h3>
            <p>Add a channel to start receiving messages</p>
            <button className="btn btn-primary" onClick={() => {
              const firstAllowed = visibleChannelIcons[0]?.[0] || 'whatsapp';
              setNewChannel({ ...defaultNewChannel, type: firstAllowed });
              setShowAdd(true);
            }}><Plus size={15} /> Add First Channel</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {channels.map(ch => {
              const info = channelInfo[ch.type] || channelInfo.whatsapp;
              return (
                <div key={ch._id} className="card" style={{ padding: '18px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: info.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {info.Icon ? <info.Icon size={22} /> : <Radio size={20} color={info.color} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{ch.name}</span>
                        {statusBadge(ch.status)}
                        <span className="badge badge-blue">{info.label}</span>
                        {ch.accessType === 'private' && (
                          <span style={{ fontSize: '0.7rem', padding: '2px 7px', borderRadius: 99, background: '#fffbeb', color: '#92400e', fontWeight: 700, border: '1px solid #fcd34d' }}>
                            🔒 Private
                          </span>
                        )}
                        {getChannelDetail(ch)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 14 }}>
                        {ch.lastConnectedAt && <span>Connected {new Date(ch.lastConnectedAt).toLocaleDateString()}</span>}
                        <span>{ch.stats?.totalMessagesReceived || 0} messages received</span>
                        {ch.errorMessage && ch.status === 'error' && (
                          <span style={{ color: 'var(--red)' }}>Error: {ch.errorMessage}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {ch.status === 'pending_qr' && ch.type === 'whatsapp' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => fetchQR(ch._id)}>
                          <QrCode size={13} /> View QR
                        </button>
                      )}
                      {['disconnected', 'error'].includes(ch.status) && (
                        <button className="btn btn-primary btn-sm" disabled={connecting === ch._id} onClick={() => handleConnect(ch)}>
                          <Plug size={13} /> {connecting === ch._id ? 'Connecting...' : 'Connect'}
                        </button>
                      )}
                      {ch.status === 'connected' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleDisconnect(ch._id)}>
                          <Unplug size={13} /> Disconnect
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowSettings(ch)}>
                        <Settings size={13} />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(ch._id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {/* Partner app linked via "Connect with AINA Suite" */}
                  {connectedApps[ch._id] && (
                    <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(99,102,241,0.08)', borderRadius: 10, border: '1px solid rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <Link2 size={16} color="var(--primary)" />
                      <div style={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}>
                        Connected to <strong>{connectedApps[ch._id].app?.name || 'a partner app'}</strong>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.74rem', wordBreak: 'break-all' }}>
                          Replies are sent to {connectedApps[ch._id].webhookUrl}
                        </div>
                      </div>
                      {user?.role === 'merchant' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleRevokeApp(connectedApps[ch._id])}>
                          <Unplug size={13} /> Disconnect app
                        </button>
                      )}
                    </div>
                  )}
                  {/* Webchat embed code & guide */}
                  {ch.type === 'webchat' && ch.status === 'connected' && (
                    <div style={{ marginTop: 14, padding: '14px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>Add to your website</div>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                        Copy the code below and paste it in your website's HTML, just before the closing <code style={{ background: 'var(--border)', padding: '1px 4px', borderRadius: 4 }}>&lt;/body&gt;</code> tag.
                      </p>
                      <CopyableUrl url={`<script src="${WEBHOOK_BASE_URL}/widget.js" data-channel-id="${ch._id}" data-api="${WEBHOOK_BASE_URL}/api" defer></script>`} />
                      <div style={{ marginTop: 12, fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        <strong>Example:</strong>
                      </div>
                      <div style={{ marginTop: 6, padding: '10px 12px', background: '#1e293b', borderRadius: 8, fontFamily: 'monospace', fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.7, overflowX: 'auto' }}>
                        <div style={{ color: '#64748b' }}>&lt;!-- Your website HTML --&gt;</div>
                        <div>&lt;html&gt;</div>
                        <div>&nbsp;&nbsp;&lt;head&gt;...&lt;/head&gt;</div>
                        <div>&nbsp;&nbsp;&lt;body&gt;</div>
                        <div style={{ color: '#64748b' }}>&nbsp;&nbsp;&nbsp;&nbsp;&lt;!-- Your website content --&gt;</div>
                        <div>&nbsp;&nbsp;&nbsp;&nbsp;...</div>
                        <div style={{ color: '#22c55e', fontWeight: 600 }}>&nbsp;&nbsp;&nbsp;&nbsp;&lt;script src="{WEBHOOK_BASE_URL}/widget.js" data-channel-id="{ch._id}" data-api="{WEBHOOK_BASE_URL}/api" defer&gt;&lt;/script&gt;</div>
                        <div>&nbsp;&nbsp;&lt;/body&gt;</div>
                        <div>&lt;/html&gt;</div>
                      </div>
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[
                          { icon: '1.', text: 'Copy the script tag above (click to copy)' },
                          { icon: '2.', text: 'Open your website\'s HTML file or CMS editor' },
                          { icon: '3.', text: 'Paste it before </body> tag' },
                          { icon: '4.', text: 'Save & refresh your website — chat bubble will appear!' },
                        ].map((s, i) => (
                          <div key={i} style={{ display: 'flex', gap: 8, fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                            <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{s.icon}</span>
                            <span>{s.text}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(34,197,94,0.08)', borderRadius: 6, border: '1px solid rgba(34,197,94,0.2)' }}>
                        <div style={{ fontSize: '0.75rem', color: '#16a34a' }}>
                          Works with any website — WordPress, Shopify, Wix, Squarespace, custom HTML, React, or any platform that lets you add custom scripts.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Channel Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Add New Channel</span>
              <button className="modal-close" onClick={() => setShowAdd(false)}><X size={18} /></button>
            </div>

            {/* Plan summary in modal */}
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border)', marginBottom: 14, fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-muted)' }}>Channels used:</span>
              <span style={{ fontWeight: 700, color: totalAtLimit ? 'var(--red)' : 'var(--text)' }}>
                {totalUsed} / {maxChannels === -1 ? 'Unlimited' : maxChannels}
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Channel Name</label>
              <input className="form-input" placeholder="e.g. Main Support Line" value={newChannel.name}
                onChange={e => setNewChannel(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Channel Type</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {visibleChannelIcons.map(([key, val]) => {
                  const blocked = typeAtLimit(key);
                  const used = countByType[key] || 0;
                  const limit = planLimits.channelTypeLimits?.[key] || 0;
                  return (
                    <div key={key}
                      onClick={() => !blocked && setNewChannel(p => ({ ...p, type: key }))}
                      title={blocked ? `${val.label} limit reached (${used}/${limit})` : ''}
                      style={{
                        padding: '14px', borderRadius: 'var(--radius-sm)',
                        cursor: blocked ? 'not-allowed' : 'pointer',
                        opacity: blocked ? 0.45 : 1,
                        border: `2px solid ${newChannel.type === key ? val.color : 'var(--border)'}`,
                        background: newChannel.type === key ? val.bg : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        position: 'relative',
                      }}>
                      {val.Icon && <val.Icon size={20} color={newChannel.type === key && !blocked ? val.color : '#9CA3AF'} />}
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: newChannel.type === key && !blocked ? val.color : 'inherit' }}>{val.label}</div>
                      {blocked && (
                        <div style={{ position: 'absolute', top: 4, right: 6, fontSize: '0.62rem', fontWeight: 700, color: 'var(--red)', background: 'rgba(239,68,68,0.1)', padding: '1px 5px', borderRadius: 8 }}>
                          {used}/{limit} max
                        </div>
                      )}
                      {!blocked && limit > 0 && (
                        <div style={{ position: 'absolute', top: 4, right: 6, fontSize: '0.62rem', fontWeight: 600, color: val.color, background: val.color + '15', padding: '1px 5px', borderRadius: 8 }}>
                          {used}/{limit}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* WhatsApp specific */}
            {newChannel.type === 'whatsapp' && (
              <div className="form-group">
                <label className="form-label">Connection Type</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[
                    { value: 'aina', label: 'AINA API', desc: 'Free, scan QR code' },
                    { value: 'official', label: 'Official API', desc: 'Meta Business Platform' },
                  ].map(opt => (
                    <div key={opt.value}
                      onClick={() => setNewChannel(p => ({ ...p, connectionType: opt.value }))}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                        border: `1px solid ${newChannel.connectionType === opt.value ? 'var(--primary)' : 'var(--border)'}`,
                        background: newChannel.connectionType === opt.value ? 'rgba(99,102,241,0.06)' : 'transparent',
                      }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{opt.label}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WhatsApp Official API fields */}
            {newChannel.type === 'whatsapp' && newChannel.connectionType === 'official' && (
              <>
                <div className="form-group">
                  <label className="form-label">Phone Number ID</label>
                  <input className="form-input" placeholder="e.g. 100234567890123"
                    value={newChannel.whatsappOfficial.phoneNumberId}
                    onChange={e => setNewChannel(p => ({ ...p, whatsappOfficial: { ...p.whatsappOfficial, phoneNumberId: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Access Token</label>
                  <input className="form-input" type="password" placeholder="Paste your permanent access token"
                    value={newChannel.whatsappOfficial.accessToken}
                    onChange={e => setNewChannel(p => ({ ...p, whatsappOfficial: { ...p.whatsappOfficial, accessToken: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">WABA ID (optional)</label>
                  <input className="form-input" placeholder="WhatsApp Business Account ID"
                    value={newChannel.whatsappOfficial.wabaId}
                    onChange={e => setNewChannel(p => ({ ...p, whatsappOfficial: { ...p.whatsappOfficial, wabaId: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Webhook Verify Token</label>
                  <input className="form-input" placeholder="Enter a token (must match Meta dashboard)"
                    value={newChannel.whatsappOfficial.verifyToken}
                    onChange={e => setNewChannel(p => ({ ...p, whatsappOfficial: { ...p.whatsappOfficial, verifyToken: e.target.value } }))} />
                </div>
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <strong>Setup steps:</strong><br />
                    1. Go to <strong>Meta for Developers</strong> &rarr; Select your app<br />
                    2. Under WhatsApp &rarr; Configuration, set the <strong>Webhook URL</strong> and <strong>Verify Token</strong><br />
                    3. The webhook URL will be shown after you create this channel<br />
                    4. Use the same verify token you enter above in the Meta dashboard
                  </div>
                </div>
              </>
            )}

            {/* Telegram specific */}
            {newChannel.type === 'telegram' && (
              <>
                <div className="form-group">
                  <label className="form-label">Bot Token</label>
                  <input className="form-input" placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                    value={newChannel.telegram.botToken}
                    onChange={e => setNewChannel(p => ({ ...p, telegram: { ...p.telegram, botToken: e.target.value } }))} />
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Get your bot token from <strong>@BotFather</strong> on Telegram. Send /newbot to create a new bot.
                  </div>
                </div>
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <strong>Setup steps:</strong><br />
                    1. Open Telegram and search for @BotFather<br />
                    2. Send /newbot and follow the prompts<br />
                    3. Copy the bot token and paste it above<br />
                    4. After creating the channel, click Connect to activate
                  </div>
                </div>
              </>
            )}

            {/* Facebook specific */}
            {newChannel.type === 'facebook' && (
              <>
                <div className="form-group">
                  <label className="form-label">Page ID</label>
                  <input className="form-input" placeholder="e.g. 123456789012345"
                    value={newChannel.facebook.pageId}
                    onChange={e => setNewChannel(p => ({ ...p, facebook: { ...p.facebook, pageId: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Page Access Token</label>
                  <input className="form-input" type="password" placeholder="Paste your page access token"
                    value={newChannel.facebook.accessToken}
                    onChange={e => setNewChannel(p => ({ ...p, facebook: { ...p.facebook, accessToken: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Webhook Verify Token</label>
                  <input className="form-input" placeholder="Enter a verify token (must match Meta dashboard)"
                    value={newChannel.facebook.verifyToken}
                    onChange={e => setNewChannel(p => ({ ...p, facebook: { ...p.facebook, verifyToken: e.target.value } }))} />
                </div>
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <strong>Setup steps:</strong><br />
                    1. Go to <strong>Meta for Developers</strong> &rarr; Create or select your app<br />
                    2. Add <strong>Messenger</strong> product to your app<br />
                    3. Under Messenger Settings, generate a <strong>Page Access Token</strong><br />
                    4. Copy the Page ID from your Facebook Page &rarr; About section<br />
                    5. Set webhook URL to: <code>{WEBHOOK_BASE_URL}/api/webhooks/facebook/{'<channelId>'}</code><br />
                    6. After creating the channel, click Connect
                  </div>
                </div>
              </>
            )}

            {/* Instagram specific */}
            {newChannel.type === 'instagram' && (
              <>
                <div className="alert alert-info" style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <strong>Tip:</strong> For best results, use a <strong>Facebook Page Access Token</strong> (starts with <code>EAA...</code>). Instagram tokens (IGAA...) may also work depending on your setup.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Instagram Business Account ID</label>
                  <input className="form-input" placeholder="e.g. 17841400123456789"
                    value={newChannel.instagram.accountId}
                    onChange={e => setNewChannel(p => ({ ...p, instagram: { ...p.instagram, accountId: e.target.value } }))} />
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Find in Graph API Explorer: <code>GET /me?fields=instagram_business_account</code>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Facebook Page Access Token</label>
                  <input className="form-input" type="password" placeholder="EAA... (Facebook Page token, NOT Instagram token)"
                    value={newChannel.instagram.accessToken}
                    onChange={e => setNewChannel(p => ({ ...p, instagram: { ...p.instagram, accessToken: e.target.value } }))} />
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Generate at: Graph API Explorer → select your <strong>Facebook Page</strong> (not App) → permissions: <code>instagram_manage_messages</code>, <code>pages_messaging</code>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Webhook Verify Token</label>
                  <input className="form-input" placeholder="Any secret string (must match Meta dashboard)"
                    value={newChannel.instagram.verifyToken}
                    onChange={e => setNewChannel(p => ({ ...p, instagram: { ...p.instagram, verifyToken: e.target.value } }))} />
                </div>
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  <div style={{ fontSize: '0.78rem', lineHeight: 1.6 }}>
                    <strong>Setup steps:</strong><br />
                    1. Link your <strong>Instagram Professional Account</strong> to a <strong>Facebook Page</strong><br />
                    2. Go to <strong>developers.facebook.com</strong> → select your app<br />
                    3. Add <strong>Instagram</strong> and <strong>Messenger</strong> products<br />
                    4. Go to <strong>Graph API Explorer</strong> → select your <strong>Page</strong> (not App)<br />
                    5. Add permissions: <code>instagram_manage_messages</code>, <code>pages_messaging</code>, <code>instagram_basic</code><br />
                    6. Click <strong>Generate Access Token</strong> (must start with <code>EAA...</code>)<br />
                    7. Get Instagram Account ID: <code>GET /me?fields=instagram_business_account</code><br />
                    8. After creating the channel, set webhook URL in Meta dashboard<br />
                    9. Click <strong>Connect</strong> to verify
                  </div>
                </div>
              </>
            )}

            {/* KakaoTalk specific */}
            {newChannel.type === 'kakaotalk' && (
              <>
                <div className="form-group">
                  <label className="form-label">App Key <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input className="form-input" placeholder="Your Kakao App REST API Key"
                    value={newChannel.kakaotalk.appKey}
                    onChange={e => setNewChannel(p => ({ ...p, kakaotalk: { ...p.kakaotalk, appKey: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Admin Key <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input className="form-input" type="password" placeholder="Your Kakao Admin Key"
                    value={newChannel.kakaotalk.adminKey}
                    onChange={e => setNewChannel(p => ({ ...p, kakaotalk: { ...p.kakaotalk, adminKey: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Bot ID (optional)</label>
                  <input className="form-input" placeholder="KakaoTalk Bot ID"
                    value={newChannel.kakaotalk.botId}
                    onChange={e => setNewChannel(p => ({ ...p, kakaotalk: { ...p.kakaotalk, botId: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Channel Public ID (optional)</label>
                  <input className="form-input" placeholder="e.g. _xkABcD"
                    value={newChannel.kakaotalk.channelPublicId}
                    onChange={e => setNewChannel(p => ({ ...p, kakaotalk: { ...p.kakaotalk, channelPublicId: e.target.value } }))} />
                </div>
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <strong>Setup steps:</strong><br />
                    1. Go to <strong>Kakao Developers</strong> (developers.kakao.com)<br />
                    2. Create an application and get the <strong>REST API Key</strong> and <strong>Admin Key</strong><br />
                    3. Enable <strong>KakaoTalk Channel</strong> in your app settings<br />
                    4. Set the webhook URL after creating this channel<br />
                    5. Click <strong>Connect</strong> to activate
                  </div>
                </div>
              </>
            )}

            {/* TikTok specific */}
            {newChannel.type === 'tiktok' && (
              <>
                <div className="form-group">
                  <label className="form-label">App ID <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input className="form-input" placeholder="TikTok Business App ID"
                    value={newChannel.tiktok.appId}
                    onChange={e => setNewChannel(p => ({ ...p, tiktok: { ...p.tiktok, appId: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">App Secret <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input className="form-input" type="password" placeholder="TikTok Business App Secret"
                    value={newChannel.tiktok.appSecret}
                    onChange={e => setNewChannel(p => ({ ...p, tiktok: { ...p.tiktok, appSecret: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Access Token <span style={{ color: 'var(--red)' }}>*</span></label>
                  <input className="form-input" type="password" placeholder="Long-lived access token"
                    value={newChannel.tiktok.accessToken}
                    onChange={e => setNewChannel(p => ({ ...p, tiktok: { ...p.tiktok, accessToken: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Business ID (optional)</label>
                  <input className="form-input" placeholder="TikTok Business Center ID"
                    value={newChannel.tiktok.businessId}
                    onChange={e => setNewChannel(p => ({ ...p, tiktok: { ...p.tiktok, businessId: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Webhook Secret (optional)</label>
                  <input className="form-input" placeholder="For webhook signature verification"
                    value={newChannel.tiktok.webhookSecret}
                    onChange={e => setNewChannel(p => ({ ...p, tiktok: { ...p.tiktok, webhookSecret: e.target.value } }))} />
                </div>
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <strong>Setup steps:</strong><br />
                    1. Go to <strong>TikTok for Business</strong> developer portal<br />
                    2. Create a Business App and get <strong>App ID</strong> and <strong>App Secret</strong><br />
                    3. Generate a long-lived <strong>Access Token</strong><br />
                    4. Configure the webhook URL after creating this channel<br />
                    5. Click <strong>Connect</strong> to activate
                  </div>
                </div>
              </>
            )}

            {/* Webchat specific */}
            {newChannel.type === 'webchat' && (
              <>
                <div className="form-group">
                  <label className="form-label">Website URL(s) <span style={{ color: 'var(--red)' }}>*</span></label>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>Enter your website URL where the widget will be embedded. Include https://</div>
                  {newChannel.webchat.allowedOrigins.map((url, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                      <input className="form-input" placeholder="https://yourwebsite.com" value={url}
                        onChange={e => {
                          const origins = [...newChannel.webchat.allowedOrigins];
                          origins[i] = e.target.value;
                          setNewChannel(p => ({ ...p, webchat: { ...p.webchat, allowedOrigins: origins } }));
                        }} />
                      {newChannel.webchat.allowedOrigins.length > 1 && (
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => {
                          const origins = newChannel.webchat.allowedOrigins.filter((_, j) => j !== i);
                          setNewChannel(p => ({ ...p, webchat: { ...p.webchat, allowedOrigins: origins } }));
                        }}>Remove</button>
                      )}
                    </div>
                  ))}
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem' }} onClick={() => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, allowedOrigins: [...p.webchat.allowedOrigins, ''] } }))}>
                    + Add another URL
                  </button>
                </div>
                <div className="form-group">
                  <label className="form-label">Widget Color</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="color" value={newChannel.webchat.widgetColor}
                      onChange={e => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, widgetColor: e.target.value } }))}
                      style={{ width: 40, height: 36, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                    <input className="form-input" value={newChannel.webchat.widgetColor} style={{ flex: 1 }}
                      onChange={e => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, widgetColor: e.target.value } }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Bubble Icon</label>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: 6 }}>Upload a custom icon image for the chat bubble. Leave empty for default chat icon.</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 52, height: 52, borderRadius: '50%', background: newChannel.webchat.widgetColor, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '2px solid var(--border)', flexShrink: 0 }}>
                      {newChannel.webchat.bubbleIcon
                        ? <img src={newChannel.webchat.bubbleIcon} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="icon" />
                        : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                      }
                    </div>
                    <div style={{ flex: 1 }}>
                      <input className="form-input" placeholder="https://example.com/icon.png" value={newChannel.webchat.bubbleIcon || ''}
                        onChange={e => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, bubbleIcon: e.target.value } }))} />
                    </div>
                    {newChannel.webchat.bubbleIcon && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                        onClick={() => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, bubbleIcon: '' } }))}>Clear</button>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Welcome Message</label>
                  <input className="form-input" value={newChannel.webchat.welcomeMessage}
                    onChange={e => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, welcomeMessage: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Input Placeholder</label>
                  <input className="form-input" value={newChannel.webchat.placeholder}
                    onChange={e => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, placeholder: e.target.value } }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Position</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {['right', 'left'].map(pos => (
                      <div key={pos} onClick={() => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, position: pos } }))}
                        style={{
                          flex: 1, padding: '10px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
                          border: `2px solid ${newChannel.webchat.position === pos ? 'var(--primary)' : 'var(--border)'}`,
                          background: newChannel.webchat.position === pos ? 'rgba(99,102,241,0.06)' : 'transparent',
                          fontWeight: 600, fontSize: '0.85rem', textTransform: 'capitalize',
                        }}>
                        {pos}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Live Agent Toggle */}
                <div className="form-group" style={{ marginTop: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <label className="form-label" style={{ marginBottom: 0 }}>Allow Agent Takeover</label>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>Show "Talk to an agent" button in widget</div>
                    </div>
                    <button className={`toggle ${newChannel.webchat.allowLiveAgent ? 'on' : ''}`}
                      onClick={() => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, allowLiveAgent: !p.webchat.allowLiveAgent } }))} />
                  </div>
                </div>

                <PrechatFormEditor
                  value={newChannel.webchat.prechatForm}
                  onChange={(pf) => setNewChannel(p => ({ ...p, webchat: { ...p.webchat, prechatForm: pf } }))}
                />

                <div style={{ marginTop: 12, padding: '14px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 10 }}>How Web Chat Widget Works</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      { n: '1', t: 'Create this channel', d: 'Click "Create Channel" below. Your widget will be ready instantly.' },
                      { n: '2', t: 'Copy the script tag', d: 'A code snippet will appear on the channel card. Click to copy it.' },
                      { n: '3', t: 'Paste in your website', d: 'Add the script tag just before the closing </body> tag in your website\'s HTML.' },
                      { n: '4', t: 'Chat bubble appears', d: 'A chat button will show on your website. Visitors click it to start chatting.' },
                      { n: '5', t: 'AI replies automatically', d: 'Your AI assistant answers questions using your connected data (sheets, files, etc.).' },
                      { n: '6', t: 'View in your Inbox', d: 'All website conversations appear in your Inbox, just like WhatsApp or Telegram.' },
                    ].map(step => (
                      <div key={step.n} style={{ display: 'flex', gap: 10 }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.72rem', flexShrink: 0, marginTop: 1 }}>{step.n}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{step.t}</div>
                          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 1 }}>{step.d}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Channel Access */}
            <div style={{ margin: '14px 0 4px', padding: '12px 14px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Channel Access</div>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: 10 }}>
                Private channels require you to approve new customers before AI responds.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ v: 'public', label: '🌐 Public', desc: 'AI responds to everyone immediately' }, { v: 'private', label: '🔒 Private', desc: 'Merchant must approve each new customer' }].map(opt => (
                  <div key={opt.v} onClick={() => setNewChannel(p => ({ ...p, accessType: opt.v }))}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${newChannel.accessType === opt.v ? (opt.v === 'private' ? '#f59e0b' : 'var(--primary)') : 'var(--border)'}`,
                      background: newChannel.accessType === opt.v ? (opt.v === 'private' ? '#fffbeb' : 'var(--primary-light)') : 'var(--card-bg)',
                    }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: newChannel.accessType === opt.v ? (opt.v === 'private' ? '#92400e' : 'var(--primary)') : 'var(--text)' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate}><Plus size={15} /> Create Channel</button>
            </div>
          </div>
        </div>
      )}

      {/* Channel Settings Modal */}
      {showSettings && <ChannelSettingsModal channel={showSettings} onClose={() => setShowSettings(null)} onSave={handleUpdateSettings} />}

      {/* QR Code Modal */}
      {showQR && (
        <div className="modal-overlay" onClick={() => setShowQR(null)}>
          <div className="modal" style={{ maxWidth: 360, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Scan WhatsApp QR Code</span>
              <button className="modal-close" onClick={() => setShowQR(null)}><X size={18} /></button>
            </div>
            {qrData ? (
              <>
                <div className="qr-container">
                  <img src={qrData} alt="QR Code" />
                  <div style={{ fontSize: '0.78rem', color: '#666' }}>Open WhatsApp &rarr; Linked Devices &rarr; Link a Device</div>
                </div>
                <p style={{ marginTop: 16, fontSize: '0.82rem' }}>
                  QR refreshes every 60 seconds. Keep this window open until connected.
                </p>
              </>
            ) : (
              <div className="flex-center" style={{ flexDirection: 'column', gap: 12, padding: 40 }}>
                <div className="spinner" />
                <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Generating QR code...</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Settings modal - edit channel credentials & config
 */
function ChannelSettingsModal({ channel, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(channel.customAiConfig?.promptId || '');
  const [allowLiveAgent, setAllowLiveAgent] = useState(channel.allowLiveAgent !== false);
  const [accessType, setAccessType] = useState(channel.accessType || 'public');

  useEffect(() => {
    merchantAPI.getPrompts().then(({ data }) => setPrompts(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (channel.type === 'telegram') {
      setForm({ telegram: { botToken: channel.telegram?.botToken || '' } });
    } else if (channel.type === 'facebook') {
      setForm({
        facebook: {
          pageId: channel.facebook?.pageId || '',
          accessToken: channel.facebook?.accessToken || '',
          verifyToken: channel.facebook?.verifyToken || '',
        },
      });
    } else if (channel.type === 'instagram') {
      setForm({
        instagram: {
          accountId: channel.instagram?.accountId || '',
          accessToken: channel.instagram?.accessToken || '',
          verifyToken: channel.instagram?.verifyToken || '',
        },
      });
    } else if (channel.type === 'kakaotalk') {
      setForm({
        kakaotalk: {
          appKey: channel.kakaotalk?.appKey || '',
          adminKey: channel.kakaotalk?.adminKey || '',
          botId: channel.kakaotalk?.botId || '',
          channelPublicId: channel.kakaotalk?.channelPublicId || '',
        },
      });
    } else if (channel.type === 'tiktok') {
      setForm({
        tiktok: {
          appId: channel.tiktok?.appId || '',
          appSecret: channel.tiktok?.appSecret || '',
          accessToken: channel.tiktok?.accessToken || '',
          businessId: channel.tiktok?.businessId || '',
          webhookSecret: channel.tiktok?.webhookSecret || '',
        },
      });
    } else if (channel.type === 'whatsapp' && channel.whatsapp?.connectionType === 'official') {
      setForm({
        'whatsapp.official': {
          phoneNumberId: channel.whatsapp?.official?.phoneNumberId || '',
          accessToken: channel.whatsapp?.official?.accessToken || '',
          wabaId: channel.whatsapp?.official?.wabaId || '',
          verifyToken: channel.whatsapp?.official?.verifyToken || '',
        },
      });
    }
  }, [channel]);

  const handleSaveWithPrompt = async () => {
    // Save prompt assignment
    try {
      await merchantAPI.assignChannelPrompt(channel._id, { promptId: selectedPromptId || null });
    } catch (_) {}
    // Include live agent + access type settings
    form.allowLiveAgent = allowLiveAgent;
    form.accessType = accessType;
    onSave(channel._id, form);
  };

  const info = CHANNEL_ICONS[channel.type];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: info?.color }}>Settings:</span> {channel.name}
          </span>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <CopyableUrl label="Channel ID" url={channel._id} />

        {/* AI Prompt selector */}
        <div className="form-group" style={{ marginTop: 10 }}>
          <label className="form-label">AI Prompt</label>
          <select className="form-input" value={selectedPromptId || ''} onChange={e => setSelectedPromptId(e.target.value)}>
            <option value="">Global Prompt (default)</option>
            {prompts.map(p => <option key={p._id} value={p._id}>{p.name}{p.isDefault ? ' (default)' : ''}</option>)}
          </select>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 4 }}>
            {selectedPromptId
              ? `This channel uses a custom prompt: "${prompts.find(p => p._id === selectedPromptId)?.name || ''}"`
              : 'Using the global system prompt from AI Settings'}
          </div>
          {prompts.length === 0 && (
            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 4 }}>
              No custom prompts created yet. Go to AI Settings to create prompts.
            </div>
          )}
        </div>

        {channel.type === 'telegram' && (
          <div className="form-group">
            <label className="form-label">Bot Token</label>
            <input className="form-input" value={form.telegram?.botToken || ''}
              onChange={e => setForm(p => ({ ...p, telegram: { ...p.telegram, botToken: e.target.value } }))} />
            <CopyableUrl label="Webhook URL" url={`${WEBHOOK_BASE_URL}/api/webhooks/telegram/${channel._id}`} />
          </div>
        )}

        {channel.type === 'facebook' && (
          <>
            <div className="form-group">
              <label className="form-label">Page ID</label>
              <input className="form-input" value={form.facebook?.pageId || ''}
                onChange={e => setForm(p => ({ ...p, facebook: { ...p.facebook, pageId: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Page Access Token</label>
              <input className="form-input" type="password" value={form.facebook?.accessToken || ''}
                onChange={e => setForm(p => ({ ...p, facebook: { ...p.facebook, accessToken: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Webhook Verify Token</label>
              <input className="form-input" value={form.facebook?.verifyToken || ''}
                onChange={e => setForm(p => ({ ...p, facebook: { ...p.facebook, verifyToken: e.target.value } }))} />
            </div>
            <CopyableUrl label="Webhook URL" url={`${WEBHOOK_BASE_URL}/api/webhooks/facebook/${channel._id}`} />
          </>
        )}

        {channel.type === 'instagram' && (
          <>
            <div className="alert alert-info" style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.78rem' }}>
                <strong>Tip:</strong> A <strong>Facebook Page Access Token</strong> (<code>EAA...</code>) is recommended. Instagram tokens may also work.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Instagram Business Account ID</label>
              <input className="form-input" value={form.instagram?.accountId || ''}
                onChange={e => setForm(p => ({ ...p, instagram: { ...p.instagram, accountId: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Facebook Page Access Token</label>
              <input className="form-input" type="password" placeholder="EAA..." value={form.instagram?.accessToken || ''}
                onChange={e => setForm(p => ({ ...p, instagram: { ...p.instagram, accessToken: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Webhook Verify Token</label>
              <input className="form-input" value={form.instagram?.verifyToken || ''}
                onChange={e => setForm(p => ({ ...p, instagram: { ...p.instagram, verifyToken: e.target.value } }))} />
            </div>
            <CopyableUrl label="Webhook URL" url={`${WEBHOOK_BASE_URL}/api/webhooks/instagram/${channel._id}`} />
          </>
        )}

        {channel.type === 'kakaotalk' && (
          <>
            <div className="form-group">
              <label className="form-label">App Key</label>
              <input className="form-input" value={form.kakaotalk?.appKey || ''}
                onChange={e => setForm(p => ({ ...p, kakaotalk: { ...p.kakaotalk, appKey: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Admin Key</label>
              <input className="form-input" type="password" value={form.kakaotalk?.adminKey || ''}
                onChange={e => setForm(p => ({ ...p, kakaotalk: { ...p.kakaotalk, adminKey: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Bot ID</label>
              <input className="form-input" value={form.kakaotalk?.botId || ''}
                onChange={e => setForm(p => ({ ...p, kakaotalk: { ...p.kakaotalk, botId: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Channel Public ID</label>
              <input className="form-input" value={form.kakaotalk?.channelPublicId || ''}
                onChange={e => setForm(p => ({ ...p, kakaotalk: { ...p.kakaotalk, channelPublicId: e.target.value } }))} />
            </div>
            <CopyableUrl label="Webhook URL" url={`${WEBHOOK_BASE_URL}/api/webhooks/kakaotalk/${channel._id}`} />
          </>
        )}

        {channel.type === 'tiktok' && (
          <>
            <div className="form-group">
              <label className="form-label">App ID</label>
              <input className="form-input" value={form.tiktok?.appId || ''}
                onChange={e => setForm(p => ({ ...p, tiktok: { ...p.tiktok, appId: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">App Secret</label>
              <input className="form-input" type="password" value={form.tiktok?.appSecret || ''}
                onChange={e => setForm(p => ({ ...p, tiktok: { ...p.tiktok, appSecret: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Access Token</label>
              <input className="form-input" type="password" value={form.tiktok?.accessToken || ''}
                onChange={e => setForm(p => ({ ...p, tiktok: { ...p.tiktok, accessToken: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Business ID</label>
              <input className="form-input" value={form.tiktok?.businessId || ''}
                onChange={e => setForm(p => ({ ...p, tiktok: { ...p.tiktok, businessId: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Webhook Secret</label>
              <input className="form-input" value={form.tiktok?.webhookSecret || ''}
                onChange={e => setForm(p => ({ ...p, tiktok: { ...p.tiktok, webhookSecret: e.target.value } }))} />
            </div>
            <CopyableUrl label="Webhook URL" url={`${WEBHOOK_BASE_URL}/api/webhooks/tiktok/${channel._id}`} />
          </>
        )}

        {channel.type === 'whatsapp' && channel.whatsapp?.connectionType === 'official' && (
          <>
            <div className="form-group">
              <label className="form-label">Phone Number ID</label>
              <input className="form-input" value={form['whatsapp.official']?.phoneNumberId || ''}
                onChange={e => setForm(p => ({ ...p, 'whatsapp.official': { ...p['whatsapp.official'], phoneNumberId: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Access Token</label>
              <input className="form-input" type="password" value={form['whatsapp.official']?.accessToken || ''}
                onChange={e => setForm(p => ({ ...p, 'whatsapp.official': { ...p['whatsapp.official'], accessToken: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">WABA ID</label>
              <input className="form-input" value={form['whatsapp.official']?.wabaId || ''}
                onChange={e => setForm(p => ({ ...p, 'whatsapp.official': { ...p['whatsapp.official'], wabaId: e.target.value } }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Webhook Verify Token</label>
              <input className="form-input" value={form['whatsapp.official']?.verifyToken || ''}
                onChange={e => setForm(p => ({ ...p, 'whatsapp.official': { ...p['whatsapp.official'], verifyToken: e.target.value } }))} />
            </div>
            <CopyableUrl label="Webhook URL" url={`${WEBHOOK_BASE_URL}/api/webhooks/whatsapp/${channel._id}`} />
          </>
        )}

        {channel.type === 'whatsapp' && channel.whatsapp?.connectionType === 'aina' && (
          <div className="alert alert-info">
            <div style={{ fontSize: '0.8rem' }}>
              WhatsApp sessions via AINA API are managed via QR code scanning. No additional settings needed. Use the Connect button to generate a new QR code.
            </div>
          </div>
        )}

        {/* Channel Access Type */}
        <div style={{ marginTop: 12, padding: '12px 14px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6 }}>Channel Access</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: 10 }}>
            Private channels require merchant approval before the AI responds to new customers.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['public', 'private'].map(t => (
              <button key={t} onClick={() => setAccessType(t)}
                style={{
                  flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${accessType === t ? (t === 'private' ? '#f59e0b' : 'var(--primary)') : 'var(--border)'}`,
                  background: accessType === t ? (t === 'private' ? '#fffbeb' : 'var(--primary-light)') : 'var(--card-bg)',
                  color: accessType === t ? (t === 'private' ? '#92400e' : 'var(--primary)') : 'var(--text-muted)',
                  fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', textTransform: 'capitalize',
                }}>
                {t === 'public' ? '🌐 Public' : '🔒 Private'}
              </button>
            ))}
          </div>
          {accessType === 'private' && (
            <div style={{ marginTop: 8, padding: '7px 10px', background: '#fffbeb', borderRadius: 7, border: '1px solid #fcd34d', fontSize: '0.75rem', color: '#92400e' }}>
              New customers must be approved in the Inbox before they receive AI responses.
            </div>
          )}
        </div>

        {/* Live Agent toggle — all channels */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, padding: '12px 14px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>Allow Agent Takeover</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>
              {channel.type === 'webchat' ? 'Show "Talk to an agent" button & allow agent takeover' : 'When agent replies, AI pauses. When off, AI handles everything.'}
            </div>
          </div>
          <button className={`toggle ${allowLiveAgent ? 'on' : ''}`} onClick={() => setAllowLiveAgent(p => !p)} />
        </div>

        {/* Webchat appearance settings */}
        {channel.type === 'webchat' && (
          <div style={{ marginTop: 12, padding: '14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>Widget Appearance</div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Widget Color</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.webchat?.widgetColor || channel.webchat?.widgetColor || '#6366f1'}
                  onChange={e => setForm(p => ({ ...p, webchat: { ...(p.webchat || channel.webchat || {}), widgetColor: e.target.value } }))}
                  style={{ width: 36, height: 32, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                <input className="form-input" value={form.webchat?.widgetColor || channel.webchat?.widgetColor || '#6366f1'} style={{ flex: 1 }}
                  onChange={e => setForm(p => ({ ...p, webchat: { ...(p.webchat || channel.webchat || {}), widgetColor: e.target.value } }))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Bubble Icon</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: form.webchat?.widgetColor || channel.webchat?.widgetColor || '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                  {(form.webchat?.bubbleIcon ?? channel.webchat?.bubbleIcon)
                    ? <img src={form.webchat?.bubbleIcon ?? channel.webchat?.bubbleIcon} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" />
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                  }
                </div>
                <input className="form-input" placeholder="https://example.com/icon.png" style={{ flex: 1 }}
                  value={form.webchat?.bubbleIcon ?? channel.webchat?.bubbleIcon ?? ''}
                  onChange={e => setForm(p => ({ ...p, webchat: { ...(p.webchat || channel.webchat || {}), bubbleIcon: e.target.value } }))} />
                {(form.webchat?.bubbleIcon ?? channel.webchat?.bubbleIcon) && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: '0.75rem' }}
                    onClick={() => setForm(p => ({ ...p, webchat: { ...(p.webchat || channel.webchat || {}), bubbleIcon: '' } }))}>Clear</button>
                )}
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Welcome Message</label>
              <input className="form-input" value={form.webchat?.welcomeMessage ?? channel.webchat?.welcomeMessage ?? ''}
                onChange={e => setForm(p => ({ ...p, webchat: { ...(p.webchat || channel.webchat || {}), welcomeMessage: e.target.value } }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Placeholder Text</label>
              <input className="form-input" value={form.webchat?.placeholder ?? channel.webchat?.placeholder ?? ''}
                onChange={e => setForm(p => ({ ...p, webchat: { ...(p.webchat || channel.webchat || {}), placeholder: e.target.value } }))} />
            </div>
            <PrechatFormEditor
              value={form.webchat?.prechatForm ?? channel.webchat?.prechatForm ?? { enabled: false, mode: 'required', title: 'Before we start', subtitle: 'Please share a few details so we can help you better.', submitLabel: 'Start Chat', skipLabel: 'Skip and chat anonymously', fields: [{ key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Your name' }, { key: 'email', label: 'Email', type: 'email', required: true, placeholder: 'you@example.com' }, { key: 'phone', label: 'Phone', type: 'tel', required: false, placeholder: '+1 555 000 0000' }] }}
              onChange={(pf) => setForm(p => ({ ...p, webchat: { ...(p.webchat || channel.webchat || {}), prechatForm: pf } }))}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSaveWithPrompt}>Save Settings</button>
        </div>
      </div>
    </div>
  );
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
];

function PrechatFormEditor({ value, onChange }) {
  const v = value || { enabled: false, title: '', subtitle: '', submitLabel: 'Start Chat', fields: [] };
  const update = (patch) => onChange({ ...v, ...patch });
  const updateField = (i, patch) => {
    const fields = [...(v.fields || [])];
    fields[i] = { ...fields[i], ...patch };
    update({ fields });
  };
  const removeField = (i) => update({ fields: (v.fields || []).filter((_, j) => j !== i) });
  const addField = () => update({ fields: [...(v.fields || []), { key: `field_${(v.fields || []).length + 1}`, label: 'New field', type: 'text', required: false, placeholder: '' }] });
  const moveField = (i, dir) => {
    const fields = [...(v.fields || [])];
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    [fields[i], fields[j]] = [fields[j], fields[i]];
    update({ fields });
  };

  return (
    <div style={{ marginTop: 14, padding: '14px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: v.enabled ? 14 : 0 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Pre-chat form</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>Ask visitors for details (name, email, phone…) before they start chatting.</div>
        </div>
        <button className={`toggle ${v.enabled ? 'on' : ''}`} onClick={() => update({ enabled: !v.enabled })} />
      </div>

      {v.enabled && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label className="form-label" style={{ fontSize: '0.74rem' }}>Mode</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { v: 'required', label: 'Required', desc: 'Visitor must fill the form before chatting' },
                { v: 'optional', label: 'Optional', desc: 'Visitor can skip and chat directly' },
              ].map(opt => {
                const isOn = (v.mode || 'required') === opt.v;
                return (
                  <div key={opt.v} onClick={() => update({ mode: opt.v })}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`,
                      background: isOn ? 'var(--primary-light)' : 'var(--card-bg)',
                    }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: isOn ? 'var(--primary)' : 'var(--text)' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label className="form-label" style={{ fontSize: '0.74rem' }}>Title</label>
              <input className="form-input" value={v.title || ''} onChange={e => update({ title: e.target.value })} placeholder="Before we start" />
            </div>
            <div>
              <label className="form-label" style={{ fontSize: '0.74rem' }}>Submit Button Label</label>
              <input className="form-input" value={v.submitLabel || ''} onChange={e => update({ submitLabel: e.target.value })} placeholder="Start Chat" />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label className="form-label" style={{ fontSize: '0.74rem' }}>Subtitle / instructions</label>
            <input className="form-input" value={v.subtitle || ''} onChange={e => update({ subtitle: e.target.value })} placeholder="Please share a few details so we can help you better." />
          </div>
          {(v.mode || 'required') === 'optional' && (
            <div style={{ marginBottom: 10 }}>
              <label className="form-label" style={{ fontSize: '0.74rem' }}>Skip Button Label</label>
              <input className="form-input" value={v.skipLabel || ''} onChange={e => update({ skipLabel: e.target.value })} placeholder="Skip and chat anonymously" />
            </div>
          )}

          <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: 6 }}>Fields</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(v.fields || []).map((f, i) => (
              <div key={i} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button className="btn-icon" style={{ padding: 2, fontSize: 10 }} onClick={() => moveField(i, -1)} title="Move up">▲</button>
                    <button className="btn-icon" style={{ padding: 2, fontSize: 10 }} onClick={() => moveField(i, 1)} title="Move down">▼</button>
                  </div>
                  <input className="form-input" style={{ flex: 1, fontSize: '0.82rem' }} value={f.label} placeholder="Label"
                    onChange={e => updateField(i, { label: e.target.value })} />
                  <select className="form-input" style={{ width: 130, fontSize: '0.8rem' }} value={f.type}
                    onChange={e => updateField(i, { type: e.target.value })}>
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={!!f.required} onChange={e => updateField(i, { required: e.target.checked })} />
                    Required
                  </label>
                  <button className="btn-icon" style={{ color: '#ef4444' }} onClick={() => removeField(i)} title="Remove"><Trash2 size={14} /></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <input className="form-input" style={{ fontSize: '0.78rem' }} placeholder="Field key (e.g. name, email)"
                    value={f.key} onChange={e => updateField(i, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })} />
                  <input className="form-input" style={{ fontSize: '0.78rem' }} placeholder="Placeholder text"
                    value={f.placeholder || ''} onChange={e => updateField(i, { placeholder: e.target.value })} />
                </div>
                {f.type === 'select' && (
                  <input className="form-input" style={{ fontSize: '0.78rem', marginTop: 8 }}
                    placeholder="Options (comma separated)"
                    value={(f.options || []).join(', ')}
                    onChange={e => updateField(i, { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })} />
                )}
              </div>
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={addField}><Plus size={13} /> Add Field</button>
        </>
      )}
    </div>
  );
}
